import { shaderMaterial } from '@react-three/drei';
import * as THREE from 'three';

export const ParticleShaderMaterial = shaderMaterial(
  {
    uTime: 0,
    uMouseLight: new THREE.Vector3(0, 0, 5),
    uMouseLightColor: new THREE.Color('#4a8a9a'),
    uMouseLightIntensity: 2.5,
    uScatterStrength: 0.8, // how much particles push away from cursor
    uBaseOpacity: 0.15, // opacity when not lit
    uLitOpacity: 0.9, // opacity when fully lit
    uSphereRadius: 1.5, // radius of the central sphere (for collision avoidance)
  },
  // Vertex Shader
  /* glsl */ `
    uniform float uTime;
    uniform vec3 uMouseLight;
    uniform float uScatterStrength;
    uniform float uSphereRadius;

    attribute float aOrbitSpeed;
    attribute float aOrbitPhase;
    attribute vec3 aOrbitAxis;
    attribute float aOrbitRadius;
    attribute float aSize;

    varying float vDistToLight;
    varying float vAlpha;

    // Rodrigues' rotation formula — rotate point around arbitrary axis
    vec3 rotateAroundAxis(vec3 p, vec3 axis, float angle) {
      float c = cos(angle);
      float s = sin(angle);
      return p * c + cross(axis, p) * s + axis * dot(axis, p) * (1.0 - c);
    }

    void main() {
      // Start position on orbit circle
      float angle = uTime * aOrbitSpeed + aOrbitPhase;

      // Create initial position perpendicular to orbit axis
      vec3 perp1 = normalize(cross(aOrbitAxis, vec3(0.0, 1.0, 0.0)));
      if (length(cross(aOrbitAxis, vec3(0.0, 1.0, 0.0))) < 0.001) {
        perp1 = normalize(cross(aOrbitAxis, vec3(1.0, 0.0, 0.0)));
      }

      vec3 orbitPos = rotateAroundAxis(perp1 * aOrbitRadius, aOrbitAxis, angle);

      // Scatter away from cursor
      vec3 toParticle = orbitPos - uMouseLight;
      float distToMouse = length(toParticle);
      float scatterFalloff = 1.0 / (1.0 + distToMouse * distToMouse * 2.0);
      vec3 scatterDir = normalize(toParticle);
      orbitPos += scatterDir * scatterFalloff * uScatterStrength;

      // Prevent particles from entering the sphere
      float distToCenter = length(orbitPos);
      if (distToCenter < uSphereRadius + 0.1) {
        orbitPos = normalize(orbitPos) * (uSphereRadius + 0.1);
      }

      vDistToLight = distToMouse;

      vec4 mvPosition = modelViewMatrix * vec4(orbitPos, 1.0);

      // Size attenuation (closer = larger)
      gl_PointSize = aSize * (200.0 / -mvPosition.z);
      gl_Position = projectionMatrix * mvPosition;
    }
  `,
  // Fragment Shader
  /* glsl */ `
    uniform vec3 uMouseLightColor;
    uniform float uMouseLightIntensity;
    uniform float uBaseOpacity;
    uniform float uLitOpacity;

    varying float vDistToLight;

    void main() {
      // Circular point with soft edge
      float dist = length(gl_PointCoord - vec2(0.5));
      if (dist > 0.5) discard;

      float softEdge = 1.0 - smoothstep(0.2, 0.5, dist);

      // Brightness based on cursor distance
      float lightFalloff = 1.0 / (1.0 + vDistToLight * vDistToLight * 0.3);
      float brightness = lightFalloff * uMouseLightIntensity;

      // Interpolate between dim and lit
      float alpha = mix(uBaseOpacity, uLitOpacity, brightness) * softEdge;
      vec3 color = mix(vec3(0.05, 0.1, 0.15), uMouseLightColor, brightness);

      // Add a subtle glow core
      float core = 1.0 - smoothstep(0.0, 0.15, dist);
      color += uMouseLightColor * core * brightness * 0.5;

      gl_FragColor = vec4(color, alpha);
    }
  `,
);
